import { Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import {
  FixedSheet,
  FixedSheetContent,
  FixedSheetHeader,
  ScrollableContent,
} from "@/components/ui/sheet";
import type { FreelancerFilterFormValues } from "../utils/filterParams";
import {
  FreelancerFiltersForm,
  type FreelancerFiltersFormProps,
} from "./FreelancerFiltersForm";

export type FreelancerFiltersSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultValues: FreelancerFilterFormValues;
  /** When this key changes (e.g. after Reset), the form remounts with new defaults. */
  filtersKey?: string;
  onApply: (values: FreelancerFilterFormValues) => void;
  onReset: () => void;
  disablePriceFilter?: boolean;
  /** Forwarded to the form: hide the experience-level field (Talent Pool). */
  hideExperienceFilter?: boolean;
  /** Forwarded to the form: which page hosts the filters. */
  surface?: "search" | "pool";
  /** Forwarded to the form: accessor for the offer-subcategories hint. */
  offerContext?: FreelancerFiltersFormProps["offerContext"];
};

export function FreelancerFiltersSheet(props: FreelancerFiltersSheetProps) {
  const { t } = useI18n();

  return (
    <FixedSheet open={props.open} onOpenChange={props.onOpenChange}>
      <FixedSheetContent
        side="right"
        class="flex flex-col h-full max-h-dvh w-full bg-white"
      >
        <FixedSheetHeader class="shrink-0">
          <h2 class="text-lg font-semibold text-gray-900">
            {t("company.search.freelancers.result.filters.title")}
          </h2>
        </FixedSheetHeader>
        <ScrollableContent class="flex-1 min-h-0">
          <Show when={props.filtersKey ?? ""} keyed>
            {(_key: string) => (
              <FreelancerFiltersForm
                defaultValues={props.defaultValues}
                disablePriceFilter={props.disablePriceFilter}
                hideExperienceFilter={props.hideExperienceFilter}
                surface={props.surface}
                offerContext={props.offerContext}
                inSheet
                onSubmit={(values) => {
                  props.onApply(values);
                  props.onOpenChange(false);
                }}
                onReset={() => {
                  props.onReset();
                  props.onOpenChange(false);
                }}
              />
            )}
          </Show>
        </ScrollableContent>
      </FixedSheetContent>
    </FixedSheet>
  );
}
